import { createAsyncThunk, Dispatch } from "@reduxjs/toolkit";

import { IStoreState } from "App/types";
import { CompanyService, SubscriptionService } from "Bands/services";
import {
  BAND_ACTIONS,
  ICompany,
  ICompanyPatchData,
  SubscriptionStatus,
} from "Bands/types";
import { IDataKeys } from "shared/types";

export const createCompany = createAsyncThunk(
  BAND_ACTIONS.createCompany,
  async (company: Omit<ICompany, "id">, { rejectWithValue }) => {
    try {
      const data = await CompanyService.createCompany(company);
      const id = Object.values(data?.company)?.[0]?.attributes?.id;
      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editCompany = createAsyncThunk(
  BAND_ACTIONS.editCompany,
  async (
    {
      companyId,
      companyData,
    }: { companyId: string; companyData: ICompanyPatchData },
    { rejectWithValue }
  ) => {
    try {
      const editedCompany = await CompanyService.editCompany(
        companyId,
        companyData
      );
      return editedCompany;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getCompany = createAsyncThunk(
  BAND_ACTIONS.getCompany,
  async (companyId: string, { rejectWithValue }) => {
    try {
      const company = await CompanyService.getCompany(companyId);
      return company;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUserCompanies = createAsyncThunk(
  BAND_ACTIONS.getUserCompanies,
  async (_, { rejectWithValue, getState }) => {
    try {
      const user = (getState() as IStoreState).auth.currentUser?.id;

      if (user) {
        const data = await CompanyService.getCompanies({
          user,
        });

        // keep the key even if response is empty
        return {
          [IDataKeys.userCompanies]: data.company,
        };
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetCompanyInvoices = (
  dispatch: Dispatch<any>,
  props: {
    company?: string;
    page?: number;
    itemsPerPage?: number;
  } = {}
) => {
  props.page = props.page || 1;
  props.itemsPerPage = props.itemsPerPage || 15;

  dispatch(
    getCompanyInvoices({
      ...props,
      paginationKey: BAND_ACTIONS.getCompanyInvoices,
    })
  );
};

export const getCompanyInvoices = createAsyncThunk(
  BAND_ACTIONS.getCompanyInvoices,
  async (
    {
      paginationKey,
      ...props
    }: {
      page?: number;
      company?: string;
      paginationKey: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await CompanyService.getInvoices(props);

      // keep the key even if response is empty
      return {
        [IDataKeys.companyInvoices]: data.invoice,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getInvoice = createAsyncThunk(
  BAND_ACTIONS.getInvoice,
  async (
    props: {
      subscription?: string;
      company?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await CompanyService.getInvoices(props);
      const id = Object.values(data?.invoice)?.[0]?.attributes?.id;

      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getCompanySubscriptions = createAsyncThunk(
  BAND_ACTIONS.getCompanySubscriptions,
  async (
    { company, status }: { company: string; status?: SubscriptionStatus },
    { rejectWithValue }
  ) => {
    try {
      const data = await SubscriptionService.getSubscriptions({
        company,
        status,
      });

      // keep the key even if response is empty
      return { [IDataKeys.companySubscriptions]: data[IDataKeys.subscription] };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPaymentInfo = createAsyncThunk(
  BAND_ACTIONS.getPaymentInfo,
  async (
    {
      id,
      redirectUrl,
    }: {
      id: string;
      redirectUrl: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const data: any = await SubscriptionService.getPaymentInfo({
        id,
        redirectUrl,
      });

      const mappedBillingPortalUrls: {
        [key: string]: { billingPortalUrl: string };
      } = {};

      (Object.values(data.billingPortal)[0] as any).attributes[
        IDataKeys.subscriptions
      ].forEach(
        ({
          billingPortalUrl,
          subscription: {
            data: { id },
          },
        }: {
          billingPortalUrl: string;
          subscription: { data: { id: string } };
        }) => {
          mappedBillingPortalUrls[id] = {
            billingPortalUrl,
          };
        }
      );

      return {
        [IDataKeys.paymentInfo]: mappedBillingPortalUrls,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
